import { useThree } from "@react-three/fiber";
import type { ThreeEvent } from "@react-three/fiber";
import { Tree } from "fluid-framework";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { Raycaster, Vector2 } from "three";
import type { Group, Intersection, Object3D } from "three";

import {
  isUserAtTable,
  userMoveToTable,
} from "../features/fluid/fluid.service.js";
import { useFluidTable } from "../features/fluid/hooks/use-fluid-table.js";
import { useFluid } from "../features/fluid/hooks/use-fluid.js";
import { usePlatform } from "../features/platform-detection/use-platform.js";
import { recordErrorInSentry } from "../utils/init-sentry.js";
import { useHover } from "./use-hover.js";

interface UseTableInteractionProps {
  tableId: string;
}

type TableInteractionEvent =
  | ThreeEvent<MouseEvent | PointerEvent | TouchEvent>
  | MouseEvent;

const eventHasPoint = (
  event: TableInteractionEvent
): event is ThreeEvent<MouseEvent | PointerEvent | TouchEvent> =>
  "point" in event;

const isAvatarOverlayEvent = (event: MouseEvent): boolean =>
  event.target instanceof Element &&
  Boolean(event.target.closest("[data-office-avatar-overlay]"));

const unsubscribeNoop = () => {
  // No subscription exists while the Fluid table is unavailable.
};

export function useTableInteraction({ tableId }: UseTableInteractionProps) {
  const { fluidUserId, sharedTree } = useFluid();
  const table = useFluidTable(tableId);
  const groupRef = useRef<Group>(null);
  const { isMobileClient } = usePlatform();
  const { camera, gl, scene } = useThree();
  const raycasterRef = useRef(new Raycaster());

  const getShouldHover = useCallback(
    () => !isUserAtTable({ sharedTree, tableId, userId: fluidUserId }),
    [fluidUserId, sharedTree, tableId]
  );
  const subscribeToTable = useCallback(
    (onStoreChange: () => void) => {
      if (!table) {
        return unsubscribeNoop;
      }

      return Tree.on(table, "treeChanged", onStoreChange);
    },
    [table]
  );
  const shouldHover = useSyncExternalStore(
    subscribeToTable,
    getShouldHover,
    getShouldHover
  );
  const hovered = useHover(groupRef, shouldHover);

  const dispatchContextMenuEvent = useCallback(
    (position: { x: number; y: number; z: number }) => {
      const customEvent = new CustomEvent("table-context-menu", {
        detail: {
          position: [position.x, position.y, position.z],
          tableId,
        },
      });
      window.dispatchEvent(customEvent);
    },
    [tableId]
  );

  const handleTableClick = useCallback(
    (event: TableInteractionEvent) => {
      event.stopPropagation();

      if (!table) {
        recordErrorInSentry(
          new Error(`Table ${tableId} is missing from the Fluid office tree`)
        );
        return;
      }

      try {
        if (!isUserAtTable({ sharedTree, tableId, userId: fluidUserId })) {
          userMoveToTable({ sharedTree, tableId, userId: fluidUserId });
        }

        // User is at the table -> on mobile, open the table menu
        if (isMobileClient() && eventHasPoint(event)) {
          dispatchContextMenuEvent({
            x: event.point.x,
            y: event.point.y,
            z: event.point.z,
          });
        }
      } catch (error) {
        recordErrorInSentry(error);
      }
    },
    [
      dispatchContextMenuEvent,
      fluidUserId,
      isMobileClient,
      sharedTree,
      table,
      tableId,
    ]
  );

  const getNearestHitOnTable = useCallback(
    (event: MouseEvent): Intersection<Object3D> | null => {
      const dialog = document.querySelector(
        '[role="dialog"], [role="alertdialog"]'
      );
      if (dialog && (dialog as HTMLElement).checkVisibility?.()) {
        return null;
      }

      const canvas = gl.domElement;
      const rect = canvas.getBoundingClientRect();
      const clickX = event.clientX;
      const clickY = event.clientY;

      if (
        clickX < rect.left ||
        clickX > rect.right ||
        clickY < rect.top ||
        clickY > rect.bottom
      ) {
        return null;
      }

      const x = ((clickX - rect.left) / rect.width) * 2 - 1;
      const y = -((clickY - rect.top) / rect.height) * 2 + 1;

      raycasterRef.current.setFromCamera(new Vector2(x, y), camera);
      const [nearestHit] = raycasterRef.current.intersectObjects(
        scene.children,
        true
      );
      if (!nearestHit) {
        return null;
      }

      let current: Object3D | null = nearestHit.object;
      while (current) {
        if (current === groupRef.current) {
          return nearestHit;
        }
        current = current.parent;
      }

      return null;
    },
    [camera, gl, scene]
  );

  // Window-level click handler with raycasting to bypass HTML overlay issues
  useEffect(() => {
    if (!(groupRef.current && tableId)) {
      return;
    }

    const handleWindowClick = (event: MouseEvent) => {
      // Only handle left clicks
      if (event.button !== 0) {
        return;
      }

      if (isAvatarOverlayEvent(event)) {
        return;
      }

      const hit = getNearestHitOnTable(event);
      if (!hit) {
        return;
      }

      // This table was clicked - call the handler
      handleTableClick(event);
    };

    window.addEventListener("click", handleWindowClick);

    return () => {
      window.removeEventListener("click", handleWindowClick);
    };
  }, [tableId, getNearestHitOnTable, handleTableClick]);

  useEffect(() => {
    if (!(groupRef.current && tableId)) {
      return;
    }

    const handleWindowContextMenu = (event: MouseEvent) => {
      if (event.button !== 2) {
        return;
      }

      if (isAvatarOverlayEvent(event)) {
        return;
      }

      const hit = getNearestHitOnTable(event);
      if (!hit) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      dispatchContextMenuEvent({
        x: hit.point.x,
        y: hit.point.y,
        z: hit.point.z,
      });
    };

    window.addEventListener("contextmenu", handleWindowContextMenu);

    return () => {
      window.removeEventListener("contextmenu", handleWindowContextMenu);
    };
  }, [tableId, dispatchContextMenuEvent, getNearestHitOnTable]);

  return {
    groupRef,
    handleTableClick,
    hovered,
    table,
  };
}
